import { WebMessageAPIPromiseClient } from "../../../gen/witbooking/backoffice/webmessage/v1/webmessage_api_grpc_web_pb";
import {
  BookingWindow,
  CreateWebMessageRequest,
  DeleteWebMessageRequest,
  GetAllWebMessagesRequest,
  GetWebMessageRequest,
  UpdateWebMessageRequest,
  WebMessage,
  WebMessageTranslation,
} from "../../../gen/witbooking/backoffice/webmessage/v1/webmessage_api_pb";
import { buildGrpcClient } from "../../../lib/grpc";
import { WebMessageEntity, WebMessageEntityBookingWindowPeriod } from "../domain/webMessage.entity";
import * as google_protobuf_timestamp_pb from "google-protobuf/google/protobuf/timestamp_pb";

const grpcClient: WebMessageAPIPromiseClient = buildGrpcClient(WebMessageAPIPromiseClient);

function isString(val: unknown): val is String {
  return typeof val === "string" || val instanceof String;
}
function dateToProtoTimestamp(
  utcDate: Date | string
): google_protobuf_timestamp_pb.Timestamp | undefined {
  if (!utcDate) {
    return undefined;
  }
  const date: Date = isString(utcDate) ? new Date(utcDate) : utcDate;
  //@ts-ignore
  const timestamp = new proto.google.protobuf.Timestamp();
  timestamp.setSeconds(date.getTime() / 1000);
  return timestamp;
}

function translationDtoFromEntity(obj: WebMessageTranslation.AsObject): WebMessageTranslation {
  const result = new WebMessageTranslation();
  result.setLocale(obj.locale);
  result.setTitle(obj.title);
  result.setDescription(obj.description);
  return result;
}
function dtoFromEntity(obj: WebMessageEntity): WebMessage {
  const result = new WebMessage();
  result.setId(obj.id);
  result.setActive(obj.active);
  result.setPositionType(obj.positionType);
  result.setType(obj.type);
  result.setUnlockCode(obj.unlockCode);
  result.setShowOnlyOnUnavailable(obj.showOnlyOnUnavailable);
  if (obj.bookingWindowPeriod?.from || obj.bookingWindowPeriod?.to) {
    const bwp = new BookingWindow();
    if (obj.bookingWindowPeriod.from) {
      bwp.setStart(dateToProtoTimestamp(obj.bookingWindowPeriod.from));
    }
    if (obj.bookingWindowPeriod.to) {
      bwp.setEndInclusive(dateToProtoTimestamp(obj.bookingWindowPeriod.to));
    }
    result.setBookingWindowPeriod(bwp);
  }
  result.setTranslationsList(obj.translationsList.map(translationDtoFromEntity));
  return result;
}

function convertTimestampAsObjectToUTCString(
  val: google_protobuf_timestamp_pb.Timestamp.AsObject | undefined
): Date | undefined {
  if (val) {
    //@ts-ignore
    const from = new proto.google.protobuf.Timestamp();
    from.setNanos(val.nanos);
    from.setSeconds(val.seconds);
    return from.toDate();
  }
  return undefined;
}

function entityDateRangeFromDto(
  obj: BookingWindow.AsObject | undefined
): WebMessageEntityBookingWindowPeriod {
  return {
    from: convertTimestampAsObjectToUTCString(obj?.start),
    to: convertTimestampAsObjectToUTCString(obj?.endInclusive),
  };
}

export async function getAllEntities(token: string): Promise<WebMessageEntity[]> {
  const request = new GetAllWebMessagesRequest();
  const response = await grpcClient.getAllWebMessages(request, {
    Authorization: `Bearer ${token}`,
  });
  return response.getWebMessagesList().map(m => ({
    ...m.toObject(),
    bookingWindowPeriod: entityDateRangeFromDto(m.getBookingWindowPeriod()?.toObject()),
  }));
}

export async function getEntity(id: string, token: string): Promise<WebMessageEntity> {
  const request = new GetWebMessageRequest();
  request.setId(id);
  const response = await grpcClient.getWebMessage(request, {
    Authorization: `Bearer ${token}`,
  });
  const entity = response.getWebMessage();
  if (!entity) {
    throw new Error("Unexpected reply obtaining webmessage");
  }
  return {
    ...entity?.toObject(),
    bookingWindowPeriod: entityDateRangeFromDto(entity.getBookingWindowPeriod()?.toObject()),
  };
}

export async function updateEntity(entity: WebMessageEntity, token: string): Promise<void> {
  const request = new UpdateWebMessageRequest();
  request.setWebMessage(dtoFromEntity(entity));
  await grpcClient.updateWebMessage(request, {
    Authorization: `Bearer ${token}`,
  });
}

export async function createEntity(entity: WebMessageEntity, token: string): Promise<string> {
  const request = new CreateWebMessageRequest();
  request.setWebMessage(dtoFromEntity(entity));
  const response = await grpcClient.createWebMessage(request, {
    Authorization: `Bearer ${token}`,
  });
  const createdEntity = response.getWebMessage();
  if (!createdEntity) {
    throw new Error("Failed to create webmessage");
  }
  return createdEntity.getId();
}

export async function deleteEntity(id: string, token: string): Promise<void> {
  const request = new DeleteWebMessageRequest();
  request.setId(id);
  await grpcClient.deleteWebMessage(request, {
    Authorization: `Bearer ${token}`,
  });
}
